import type { State } from 'vibegame';
import { StepController } from './components';

export interface StepContent {
  title: string;
  description: string;
}

export interface StepUIElements {
  btnPrev: HTMLButtonElement | null;
  btnNext: HTMLButtonElement | null;
  counter: HTMLElement | null;
  title: HTMLElement | null;
  description: HTMLElement | null;
  info: HTMLElement | null;
}

export interface StepUIConfig {
  steps: StepContent[];
  elements: StepUIElements;
  renderDescription?: (el: HTMLElement, description: string) => void;
}

export function findStepElements(selector: string): StepUIElements | null {
  const container = document.querySelector(selector);
  if (!container) return null;

  return {
    btnPrev: container.querySelector('.btn-prev'),
    btnNext: container.querySelector('.btn-next'),
    counter: container.querySelector('.step-counter'),
    title: container.querySelector('.step-title'),
    description: container.querySelector('.step-description'),
    info: container.querySelector('.step-info'),
  };
}

export function setupStepUI(state: State, controllerEntity: number, config: StepUIConfig): void {
  const { steps, elements, renderDescription } = config;
  const { btnPrev, btnNext, counter, title, description, info } = elements;
  const maxStep = steps.length - 1;

  // Hide navigator for single-step canvases
  if (steps.length <= 1) {
    const navigator = btnPrev?.closest('.step-navigator') as HTMLElement | null;
    if (navigator) navigator.style.display = 'none';
    return;
  }

  const renderContent = (content: StepContent) => {
    if (title) title.textContent = content.title;
    if (description) {
      if (renderDescription) {
        renderDescription(description, content.description);
      } else {
        description.textContent = content.description;
      }
    }
  };

  const calculateMaxHeight = () => {
    if (!info) return;

    info.style.minHeight = '';

    let maxHeight = 0;
    for (const content of steps) {
      renderContent(content);
      maxHeight = Math.max(maxHeight, info.offsetHeight);
    }

    info.style.minHeight = `${maxHeight}px`;

    const currentStep = StepController.target[controllerEntity];
    renderContent(steps[currentStep] ?? { title: '', description: '' });
  };

  calculateMaxHeight();
  window.addEventListener('resize', calculateMaxHeight);
  document.fonts.ready.then(calculateMaxHeight);

  const updateUI = () => {
    const step = StepController.target[controllerEntity];

    if (btnPrev) btnPrev.disabled = step <= 0;
    if (btnNext) btnNext.disabled = step >= maxStep;

    if (counter) counter.textContent = `Step ${step + 1} of ${steps.length}`;
    renderContent(steps[step] ?? { title: '', description: '' });
  };

  updateUI();

  btnPrev?.addEventListener('click', () => {
    const step = StepController.target[controllerEntity];
    if (step > 0) {
      StepController.target[controllerEntity] = step - 1;
    }
  });

  btnNext?.addEventListener('click', () => {
    const step = StepController.target[controllerEntity];
    if (step < maxStep) {
      StepController.target[controllerEntity] = step + 1;
    }
  });

  state.registerSystem({ group: 'simulation', update: updateUI });
}
